import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Sparkles, Copy, RefreshCw } from "lucide-react";

const TASKS = [
  { k: "artist_bio",         l: "Bio d'artiste",        hint: "Nom, discipline, pays, œuvre exposée, année…" },
  { k: "site_description",   l: "Description de site",  hint: "Nom du site, ambiance, patrimoine, œuvre attendue…" },
  { k: "blog_draft",         l: "Ébauche d'article",    hint: "Titre pressenti, angle, éléments à mentionner…" },
  { k: "application_reply",  l: "Réponse à candidature", hint: "Nom du candidat, geste (adoption/mécénat/…), points personnalisés…" },
];

/**
 * Panneau Assistant IA · GPT-5.6 Sol via Universal Key.
 * Génère bios, descriptions, brouillons blog et réponses aux candidatures.
 */
export default function AiAssistantPanel() {
  const [task, setTask] = useState("artist_bio");
  const [context, setContext] = useState("");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!context.trim()) { toast.error("Ajoutez un peu de contexte."); return; }
    setLoading(true);
    setText("");
    try {
      const { data } = await api.post("/admin/ai/generate", { task_type: task, context });
      setText(data.text || "");
      toast.success("Texte généré (GPT-5.6 Sol)");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyText = async () => {
    if (!text) return;
    await navigator.clipboard.writeText(text);
    toast.success("Copié dans le presse-papier");
  };

  const current = TASKS.find(t => t.k === task);

  return (
    <section data-testid="ai-assistant-panel" className="max-w-3xl">
      <div className="meta-label boxed text-cinabre inline-flex items-center gap-2">
        <Sparkles size={12} strokeWidth={1.5} /> § Assistant IA · GPT-5.6 Sol
      </div>
      <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl leading-[0.98] tracking-tight">
        Rédiger avec <em className="italic">un souffle</em>.
      </h2>
      <p className="mt-4 text-fg/70 leading-relaxed max-w-xl">
        Assistant éditorial calibré sur le ton du Fil du Midi (sobre, littéraire, patrimonial). Idéal pour brouillons rapides à relire et affiner.
      </p>

      <div className="mt-10 space-y-6">
        {/* Type de tâche */}
        <div>
          <span className="meta-label text-fg/60">Type de contenu</span>
          <div className="mt-3 flex flex-wrap gap-2">
            {TASKS.map(t => (
              <button
                key={t.k}
                onClick={() => setTask(t.k)}
                className={`px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase border transition-colors ${task === t.k ? "border-cinabre text-cinabre bg-cinabre/[0.04]" : "border-fg/20 text-fg/60 hover:border-fg/40"}`}
                data-testid={`ai-task-${t.k}`}
              >
                {t.l}
              </button>
            ))}
          </div>
        </div>

        {/* Contexte */}
        <label className="block">
          <span className="meta-label text-fg/60">Contexte · {current?.hint}</span>
          <textarea
            value={context}
            onChange={(e) => setContext(e.target.value)}
            rows={5}
            placeholder="Ex : Peggy Renaud, artiste plasticienne française, œuvre 'L'Arbre à Vœux' exposée à Castelnau-d'Estrétefonds en 2025, sensibilité aux mythologies personnelles et à la participation collective."
            className="mt-2 w-full border border-fg/20 bg-transparent p-4 font-serif text-lg leading-relaxed focus:outline-none focus:border-cinabre transition-colors"
            data-testid="ai-context-input"
          />
        </label>

        <div className="flex items-center gap-4">
          <button onClick={generate} disabled={loading} className="btn-editorial" data-testid="ai-generate-btn">
            {loading ? <><RefreshCw size={14} className="animate-spin" /> Génération…</> : <><Sparkles size={14} /> Générer</>}
          </button>
          {text && (
            <button onClick={copyText} className="btn-editorial ghost" data-testid="ai-copy-btn">
              <Copy size={14} /> Copier
            </button>
          )}
        </div>

        {/* Résultat */}
        {text && (
          <div className="mt-8 border-l-2 border-cinabre bg-cinabre/[0.03] p-6 md:p-8" data-testid="ai-output">
            <div className="meta-label text-cinabre mb-4">§ Résultat · à relire et affiner</div>
            <div className="font-serif text-lg leading-relaxed whitespace-pre-wrap text-fg/90">{text}</div>
          </div>
        )}
      </div>
    </section>
  );
}
